import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getSuppliers,
  getSupplier,
  createSupplier,
  updateSupplier,
  deleteSupplier,
} from '../api';
import type {
  SupplierFilters,
  CreateSupplierInput,
  UpdateSupplierInput,
} from '../types';

// Helper to extract error message
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred';
}

// Query keys
export const wmsSupplierKeys = {
  all: ['wms-suppliers'] as const,
  list: (filters?: SupplierFilters) => [...wmsSupplierKeys.all, 'list', filters] as const,
  detail: (id: string) => [...wmsSupplierKeys.all, 'detail', id] as const,
  infinite: (
    search: string,
    status?: SupplierFilters['status'],
    kind?: SupplierFilters['kind'],
    tag?: SupplierFilters['tag'],
  ) => [
    ...wmsSupplierKeys.all,
    'infinite',
    search,
    status ?? 'all',
    kind ?? 'all',
    tag ?? 'none',
  ] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

export function useSuppliers(filters: SupplierFilters = {}) {
  return useQuery({
    queryKey: wmsSupplierKeys.list(filters),
    queryFn: () => getSuppliers(filters),
  });
}

const SUPPLIER_PAGE_SIZE = 100;

export function useSuppliersInfinite(
  search: string,
  status: SupplierFilters['status'] | 'all' | undefined = 'active',
  kind: SupplierFilters['kind'] | undefined = 'hardware',
  tag?: SupplierFilters['tag'],
) {
  const normalizedStatus = status === 'all' ? undefined : status;

  return useInfiniteQuery({
    queryKey: wmsSupplierKeys.infinite(search, normalizedStatus, kind, tag),
    queryFn: ({ pageParam = 0 }) => getSuppliers({
      search,
      status: normalizedStatus,
      kind,
      tag,
      page_number: pageParam,
      page_length: SUPPLIER_PAGE_SIZE,
    }),
    getNextPageParam: (lastPage) => {
      const loaded = (lastPage.page_number + 1) * lastPage.page_length;
      return loaded < lastPage.total ? lastPage.page_number + 1 : undefined;
    },
    initialPageParam: 0,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSupplier(id: string | undefined) {
  return useQuery({
    queryKey: wmsSupplierKeys.detail(id ?? ''),
    queryFn: () => getSupplier(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export function useCreateSupplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSupplierInput) => createSupplier(data),
    onSuccess: (supplier) => {
      queryClient.invalidateQueries({ queryKey: wmsSupplierKeys.all });
      toast.success(`Supplier "${supplier.name}" created successfully`);
    },
    onError: (error) => {
      toast.error('Failed to create supplier', { description: getErrorMessage(error) });
    },
  });
}

export function useUpdateSupplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSupplierInput }) => updateSupplier(id, data),
    onSuccess: (supplier, variables) => {
      queryClient.invalidateQueries({ queryKey: wmsSupplierKeys.all });
      queryClient.invalidateQueries({ queryKey: wmsSupplierKeys.detail(variables.id) });
      toast.success(`Supplier "${supplier.name}" updated successfully`);
    },
    onError: (error) => {
      toast.error('Failed to update supplier', { description: getErrorMessage(error) });
    },
  });
}

export function useDeleteSupplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteSupplier(id),
    onSuccess: (_, deletedId) => {
      queryClient.invalidateQueries({ queryKey: wmsSupplierKeys.all });
      queryClient.invalidateQueries({ queryKey: wmsSupplierKeys.detail(deletedId) });
      toast.success('Supplier deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete supplier', { description: getErrorMessage(error) });
    },
  });
}
